import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

export interface WizardStep {
  label: string;
}

// No dedicated shadcn "Stepper" primitive exists - this is a small local
// component using plain divs + existing tokens, not a new shadcn install.
// Two layouts: a compact "Step 2 of 5 · label" + segmented progress bar on
// phones (the full row of labelled steps doesn't fit and used to scroll
// sideways), and the full labelled stepper from sm up.
//
// `onStepClick` (optional) makes each step on the full stepper a button that
// jumps to it - used where steps can be visited in any order (Policies).
// Without it (the request wizard) the steps are plain, non-interactive text.
export function WizardStepper({
  steps,
  currentStep,
  onStepClick
}: {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (step: number) => void;
}) {
  const current = steps[currentStep - 1];
  return (
    <>
      <div className="space-y-2 sm:hidden">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-semibold">{current?.label}</p>
          <p className="shrink-0 text-xs text-muted-foreground">
            Step {currentStep} of {steps.length}
          </p>
        </div>
        <div className="flex gap-1" aria-hidden>
          {steps.map((step, i) => (
            <div
              key={step.label}
              className={cn(
                "h-1.5 flex-1 rounded-full",
                i < currentStep ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
      </div>

      <div className="hidden items-center overflow-x-auto pb-1 sm:flex">
        {steps.map((step, i) => {
          const stepNumber = i + 1;
          const isDone = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;
          const StepTag = onStepClick ? "button" : "div";
          return (
            <React.Fragment key={step.label}>
              <StepTag
                {...(onStepClick
                  ? {
                      type: "button" as const,
                      onClick: () => onStepClick(stepNumber),
                      "aria-current": isCurrent ? ("step" as const) : undefined
                    }
                  : {})}
                className={cn(
                  "flex shrink-0 items-center gap-2",
                  onStepClick &&
                    "rounded-md px-1 py-0.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                )}
              >
                <div
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    isDone && "bg-primary text-primary-foreground",
                    isCurrent && "border-2 border-primary text-primary",
                    !isDone &&
                      !isCurrent &&
                      "border border-border text-muted-foreground"
                  )}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" /> : stepNumber}
                </div>
                <span
                  className={cn(
                    "whitespace-nowrap text-sm",
                    isDone || isCurrent
                      ? "font-medium text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </StepTag>
              {stepNumber < steps.length && (
                <div className="mx-3 h-px w-8 shrink-0 bg-border lg:w-8" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}
